import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useReducer
} from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import { Input, COLORS } from "@onelogin/react-components";
import useWindowSize from "./utils/useWindowSizeHook";
import useOnClickOutside from "./utils/useOnClickOutside";
import useKeyDown from "./utils/useKeyDown";
import { getBorderColorDefinition } from "./utils/getBorderColorDefinition.";
import SIZES from "./utils/sizes";
import KEY_CODES from "./utils/keyCodes";

const DROPDOWN_BORDER_WIDTH = 1;
const DROPDOWN_BORDER_RADIUS = 2;

const CONTENT_FONT_SIZE = 14;
const CHEVRON_WIDTH = 12;
const CHEVRON_SPACING = 12;
const OWNED_LISTBOX_PREFIX = "owned_listbox_";

const DropdownField = styled(Input)`
  cursor: pointer;
  border-bottom-left-radius: ${props =>
    props.expanded ? "0px" : `${DROPDOWN_BORDER_RADIUS}px`};
  border-bottom-right-radius: ${props =>
    props.expanded ? "0px" : `${DROPDOWN_BORDER_RADIUS}px`};
  padding-right: ${CHEVRON_SPACING * 2 + CHEVRON_WIDTH}px;
`;

const DropdownWrap = styled.div`
  position: relative;
  width: 100%;
`;

const ItemsWrap = styled.ul`
  position: absolute;
  width: 100%;
  border: ${DROPDOWN_BORDER_WIDTH}px solid ${COLORS.GRAY_COLORS.GRAY_88};
  ${props => getBorderColorDefinition(props)};
  border-top: 0px;
  transition: border-color 0.2s;
  overflow: auto;
  border-bottom-left-radius: ${DROPDOWN_BORDER_RADIUS}px;
  border-bottom-right-radius: ${DROPDOWN_BORDER_RADIUS}px;
  background-color: #fff;
  z-index: 1; //to appear above other elements
  box-sizing: border-box;
  margin-block-start: 0;
  margin-block-end: 0;
  padding-inline-start: 0;
  //zero margin and padding for IE browsers
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const DropdownItem = styled.li`
  list-style-type: none;
  font-size: 13px;
  display: flex;
  align-items: center;
  min-height: 38px;
  padding: 0 24px;
  cursor: pointer;
  color: ${COLORS.GRAY_COLORS.GRAY_35};

  ${props =>
    props.selected
      ? css`
          background-color: ${COLORS.ONELOGIN_GRAY_COLORS.ONELOGIN_GRAY_LIGHT};
        `
      : ``}

  &:hover {
    background-color: ${COLORS.ONELOGIN_GRAY_COLORS.ONELOGIN_GRAY_LIGHT};
    color: ${COLORS.GRAY_COLORS.GRAY_25};
  }
`;

const DropdownItemHeader = styled.li`
  list-style-type: none;
  font-size: 13px;
  display: flex;
  align-items: center;
  min-height: 38px;
  padding: 0 12px;
  cursor: pointer;
  color: ${COLORS.GRAY_COLORS.GRAY_35};
  font-weight: bold;
`;

const EmptyDropdown = styled.div`
  color: ${COLORS.GRAY_COLORS.GRAY_78};
  padding: 6px 12px;
  font-size: ${CONTENT_FONT_SIZE}px;
`;

const reducer = (state, action) => {
  switch (action.type) {
    case "resetSelectedItem":
      return {
        ...state,
        selectedItem: undefined,
        index: undefined,
        fullListWithIds: []
      };
    case "nextSelectedItem": {
      const nextIndex = state.selectedItem
        ? Math.min(
            state.index + 1,
            state.fullListWithIds.length - 1 //not higher than list.length
          )
        : 0;
      return {
        ...state,
        index: nextIndex,
        selectedItem: state.fullListWithIds[nextIndex]
      };
    }
    case "previousSelectedItem": {
      const previousIndex = state.selectedItem
        ? Math.max(state.index - 1, 0)
        : state.fullListWithIds.length - 1;
      return {
        ...state,
        index: previousIndex,
        selectedItem: state.fullListWithIds[previousIndex]
      };
    }
    case "updateFullList":
      return {
        ...state,
        fullListWithIds: action.fullListWithIds,
        selectedItem: action.fullListWithIds[0],
        index: 0
      };
    default:
      return { ...state };
  }
};

const SearchDropdown = React.memo(
  ({
    searchTerm,
    onSearch,
    userList,
    roleList,
    onSelectUser,
    onSelectRole,
    size,
    disabled,
    value,
    noItemsPlaceholder,
    id = Math.random()
      .toString(36)
      .replace(/[^a-z]+/g, "")
  }) => {
    const windowSize = useWindowSize();

    //initialization
    const userListWithIds = userList.map(item => ({
      type: "user",
      userId: item.userId,
      value: item.fullName,
      email: item.email,
      username: item.username
    }));

    const roleListWithIds = roleList.map(item => ({
      type: "role",
      roleId: item.roleId,
      value: item.roleName
    }));

    const fullListWithIds = userListWithIds.concat(roleListWithIds);
    //  - find item in the list by given `value` and set is initial selected and lastSelected item
    //  -  in case of need it can be sorted/filtered here
    const selectedItem = fullListWithIds.find(item => item.value === value);

    const [state, dispatch] = useReducer(reducer, {
      //item selected by user from the list of options (either by mouseclick or enter/space key press)
      selectedItem,

      //clonned `list` of options, updated for `id` value
      fullListWithIds,
      userListWithIds,
      roleListWithIds
    });

    //handle open/closed state
    const [isOpen, setOpen] = useState(false);

    //isHover state to make two sibling elements hovered at the same time
    const [isHover, setHover] = useState(undefined);

    const dropdownWrapRef = useRef();
    const dropdownFieldRef = useRef();

    //hide dropdown on outer click
    useOnClickOutside(
      dropdownWrapRef,
      useCallback(() => setOpen(false), [setOpen])
    );

    //when item is selected in from dropdown list, fire the onChange handler with selected value
    //set lastSelectedItem so dropdown doesn't fire in case user selects the same value twice
    const doSelectionAndFocus = useCallback(
      selectedItem => {
        setOpen(false);

        if (selectedItem) {
          if (selectedItem.type === "user") {
            onSelectUser(selectedItem);
          } else if (selectedItem.type === "role") {
            onSelectRole(selectedItem);
          }
          dispatch({ type: "resetSelectedItem" });
        }
        //selection changes on **mousedown** event, so mouseclick event that happens after, makes <body> to focus.
        //putting the focus() call in settimeout makes it work at the right time (after mouseclick event happens)
        setTimeout(() => {
          dropdownFieldRef.current && dropdownFieldRef.current.focus();
        });
      },
      [dispatch, onSelectUser, onSelectRole]
    );

    useEffect(() => {
      if (!isOpen) {
        dispatch({ type: "resetSelectedItem" });
      }
    }, [isOpen]);

    useEffect(() => {
      if (searchTerm !== "") {
        setOpen(true);
      }
    }, [searchTerm]);

    const onConfirm = event => {
      event.preventDefault(); //don't scroll page (space) or submit form (enter)
      if (isOpen) {
        doSelectionAndFocus(state.selectedItem);
        setOpen(false);
      } else {
        setOpen(true);
      }
    };

    const onArrowDown = event => {
      event.preventDefault(); //do not scroll the page

      if (isOpen) {
        dispatch({ type: "nextSelectedItem" });
      } else {
        setOpen(true);
      }
    };

    const onArrowUp = event => {
      event.preventDefault(); //do not scroll the page
      if (isOpen) {
        dispatch({ type: "previousSelectedItem" });
      } else {
        setOpen(true);
      }
    };

    const onEsc = event => {
      setOpen(false);
      //even though highlighted item is set on dropdown list open, we still need to revert aria-activedescendant attribute when list closes
      dispatch({ type: "resetSelectedItem" });
    };

    useKeyDown(dropdownFieldRef, {
      [KEY_CODES.ARROW_DOWN]: onArrowDown,
      [KEY_CODES.ARROW_UP]: onArrowUp,
      [KEY_CODES.ENTER]: onConfirm,
      [KEY_CODES.ESC]: onEsc
    });

    //this is called whenever options list (dis)appears
    //when present on screen, count the available height for the list so it can scroll if there's a lot of items in the list
    const itemsWrapRef = useCallback(
      dropdownListEl => {
        const dropdownWrapEl = dropdownWrapRef.current;

        if (dropdownListEl !== null && dropdownWrapEl !== null) {
          const boxRect = dropdownWrapEl.getBoundingClientRect();

          dropdownListEl.style.maxHeight = `${windowSize.height -
            boxRect.bottom -
            DROPDOWN_BORDER_WIDTH}px`;
        }
      },
      [windowSize]
    );

    const toggleOpen = () => {
      setOpen(!isOpen);
    };

    const onItemClick = doSelectionAndFocus;

    if (fullListWithIds.length !== state.fullListWithIds.length) {
      dispatch({ type: "updateFullList", fullListWithIds });
    }

    return (
      <DropdownWrap
        onMouseEnter={() => {
          setHover(true);
        }}
        onMouseLeave={() => {
          setHover(false);
        }}
        ref={dropdownWrapRef}
      >
        <DropdownField
          id={id}
          role="listbox"
          aria-expanded={isOpen}
          expanded={isOpen}
          onMouseDown={toggleOpen}
          size={size}
          disabled={disabled}
          hovered={isHover}
          ref={dropdownFieldRef}
          aria-owns={`${OWNED_LISTBOX_PREFIX}${id}`}
          onChange={event => onSearch(event.target.value)}
          value={searchTerm}
          data-testid={`search-dropdown`}
        />

        {isOpen && fullListWithIds && (
          <ItemsWrap
            disabled={disabled}
            hovered={isHover}
            ref={itemsWrapRef}
            id={`${OWNED_LISTBOX_PREFIX}${id}`}
          >
            {userListWithIds.length > 0 && (
              <DropdownItemHeader>People</DropdownItemHeader>
            )}
            {userListWithIds.map(item => {
              const isSelected =
                state.selectedItem &&
                (item.value === state.selectedItem.value &&
                  item.type === "user");
              return (
                <DropdownItem
                  role="option"
                  onMouseDown={() => {
                    onItemClick(item);
                  }}
                  key={`user-${item.userId}`}
                  id={item.userId}
                  selected={isSelected}
                  aria-selected={isSelected}
                  data-testid={`add-user-${item.userId}`}
                >
                  {item.value}
                </DropdownItem>
              );
            })}
            {roleListWithIds.length > 0 && (
              <DropdownItemHeader key={"role-list-header"}>
                Role
              </DropdownItemHeader>
            )}
            {roleListWithIds.map(item => {
              const isSelected =
                state.selectedItem &&
                (item.value === state.selectedItem.value &&
                  item.type === "role");
              return (
                <DropdownItem
                  role="option"
                  onMouseDown={() => {
                    onItemClick(item);
                  }}
                  key={`role-${item.roleId}`}
                  id={item.roleId}
                  selected={isSelected}
                  aria-selected={isSelected}
                  data-testid={`add-role-${item.roleId}`}
                >
                  {item.value}
                </DropdownItem>
              );
            })}
            {fullListWithIds.length === 0 && (
              <EmptyDropdown>
                <i>{noItemsPlaceholder}</i>
              </EmptyDropdown>
            )}
          </ItemsWrap>
        )}
      </DropdownWrap>
    );
  }
);

SearchDropdown.defaultProps = {
  disabled: false,
  size: SIZES.MEDIUM,
  noItemsPlaceholder: "No person or role found",
  list: []
};

SearchDropdown.propTypes = {
  searchTerm: PropTypes.string,
  userList: PropTypes.array,
  roleList: PropTypes.array,
  onSearch: PropTypes.func.isRequired,
  onSelectUser: PropTypes.func.isRequired,
  onSelectRole: PropTypes.func.isRequired
};

export default SearchDropdown;
